import { AssigneeType, PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { ArticlePostPreviewActionTypes, useArticlePostPreviewContext } from '../ArticlePostPreviewContext'
import { usePostProperties } from '../usePostProperties'
import Styles from './MobilePostPreviewHeader.module.scss'
import { Spinner } from 'common/Spinner/Spinner'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import cx from 'classnames'
import { Button } from 'blaze-ui/Button'
import ArrowSwitchHorizontal from 'blaze-ui/icons/20/ArrowSwitchHorizontal'
import { CrossPostDrawer } from '../PostPreviewBody/CrossPostDrawer/CrossPostDrawer'
import { Can } from 'components/Can'
import { useAuthedArticle } from '../hooks'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { AutoPostScheduled, PostButton } from '../PostPreviewBody/Actions/PostButton'
import { platformKeyByChannel, SocialChannelType } from 'common/ShareSheetV2/Screens/Default/Default'
import { UserIntent } from 'components/PostStatusDropdown/Types'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { ConnectButton } from '../ConnectButton/ConnectButton'
import { useScheduledPost } from 'hooks/queries/scheduledPosts/useScheduledPost'
import { ButtonLink } from 'blaze-ui/Button/ButtonLink'
import WordPressLogo from 'almanac-ui/icons/24/WordPressLogo'
import { usePaywallGuard } from 'hooks/usePaywallGuard'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'

export const MobilePostPreviewHeader = observer(() => {
  const { drawersStore } = useRootStore()
  const {
    state: { loading },
    almanacFile,
    validationErrors,
    dispatch,
  } = useArticlePostPreviewContext()
  const { data: scheduledPost } = useScheduledPost({ id: almanacFile.scheduled_post_id })
  const guardedHandleCrossPost = usePaywallGuard(() => drawersStore.openDrawer(CrossPostDrawer))

  const { data } = useExternalIntegrations()
  const integrations = data?.external_integrations
  const authedArticle = useAuthedArticle(almanacFile)
  const currentWorkspaceId = useCurrentWorkspaceId()
  const workspace = useCurrentWorkspace()

  // Don't show cross post button for autopilot users (is_virtual_marketer = true)
  const isAutopilotUser = workspace?.is_virtual_marketer

  const notPostable = validationErrors.length > 0
  const hasIntegrationForChannel = integrations?.some(
    (integration) => integration.provider === platformKeyByChannel[almanacFile.channel as SocialChannelType],
  )
  const assignee = { id: almanacFile.id, type: AssigneeType.Article }
  const afterExecute = (intent: UserIntent) => {
    if (intent === UserIntent.ScheduleAutoPost) {
      dispatch({ type: ArticlePostPreviewActionTypes.SET_EDITING, payload: false })
    }
  }

  const { status } = usePostProperties({
    id: almanacFile.id,
    type: AssigneeType.Article,
  })

  if (loading) {
    return (
      <div className={cx(Styles.container, Styles.loading)}>
        <Spinner />
      </div>
    )
  }

  if (status?.value === PostStatusPropertyValue.AutoPostScheduled)
    return (
      <div className={Styles.container}>
        <AutoPostScheduled
          assignee={assignee}
          afterExecute={afterExecute}
          size="md"
          className={Styles.autoPostScheduled}
          variant="ghost"
          showUnschedule
        />
      </div>
    )

  return (
    <div className={Styles.container}>
      {status?.value === PostStatusPropertyValue.Draft && hasIntegrationForChannel ? (
        <ConnectButton variant="ghost" margin="unset" isDisabled={scheduledPost?.is_posting} />
      ) : (
        !isAutopilotUser && (
          <Button
            frontIcon={ArrowSwitchHorizontal}
            variant="ghost"
            isDisabled={loading}
            onPress={guardedHandleCrossPost}
          >
            Cross Post
          </Button>
        )
      )}
      {status?.value === PostStatusPropertyValue.Posted && scheduledPost?.post_url ? (
        <ButtonLink variant="ghost" isDisabled={loading} to={scheduledPost.post_url}>
          <div className={Styles.buttonLink}>
            <WordPressLogo size={20} />
            See Post
          </div>
        </ButtonLink>
      ) : (
        <Can I="edit" a={authedArticle} inWorkspace={currentWorkspaceId}>
          <PostButton
            channel={almanacFile.channel as SocialChannelType}
            variant="ghost"
            size="md"
            disabledKeys={notPostable ? [UserIntent.ScheduleAutoPost, UserIntent.PostNow] : []}
            hasIntegrationForChannel={hasIntegrationForChannel}
            afterExecute={afterExecute}
            onAction={(action) => {
              if (action === UserIntent.EditContent) {
                dispatch({ type: ArticlePostPreviewActionTypes.SET_EDITING, payload: true })
              }
            }}
            assignee={assignee}
          />
        </Can>
      )}
    </div>
  )
})
